import React from 'react';
import { Button, Image, List, Skeleton, Space, Upload, UploadFile } from 'antd';
import { useLazyGetDocumentQuery } from 'app/features/shared/api';
import { useEffect, useState } from 'react';
import Icon, { DeleteFilled, FileUnknownFilled } from '@ant-design/icons';
import { ReactComponent as PDFIcon } from 'assets/icons/extensions/pdf.svg';
import { ReactComponent as DOCIcon } from 'assets/icons/extensions/doc.svg';
import { ReactComponent as XLSIcon } from 'assets/icons/extensions/xls.svg';
import { formatBytes } from 'app/utils';

export const FILE_TYPE_ICON = {
  word: <Icon style={{ fontSize: 48 }} component={DOCIcon} />, // <FileWordFilled style={{ color: 'blue' }} />,
  excel: <Icon style={{ fontSize: 48 }} component={XLSIcon} />,
  pdf: <Icon style={{ fontSize: 48 }} component={PDFIcon} />,
  unknown: <FileUnknownFilled style={{ fontSize: 48 }} />
};

export const FileItem = ({ disabled, file, onDelete }: any) => {
  const [loading, setLoading] = useState(true);
  const [fileInfo, setFileInfo] = useState(null);
  const [fetchDocument] = useLazyGetDocumentQuery();

  const renderFile = () => {
    const { mineType, type } = fileInfo;
    const fileType = mineType || type;
    if (!fileType) return null;
    if (['application/pdf'].includes(fileType)) {
      return FILE_TYPE_ICON.pdf;
    } else if (
      [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ].includes(fileType)
    ) {
      return FILE_TYPE_ICON.word;
    } else if (
      [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ].includes(fileType)
    ) {
      return FILE_TYPE_ICON.excel;
    }
    if (fileType.startsWith('image/')) {
      return <Image preview={false} src={fileInfo?.url} width={50}></Image>;
    }
    return FILE_TYPE_ICON.unknown;
  };
  const handleGetFileInfo = async (file) => {
    const response = await fetchDocument(file).unwrap();
    if (response.message) {
      setFileInfo(response.data);
    }
    setLoading(false);
  };
  useEffect(() => {
    if (typeof file !== 'object' && !fileInfo) {
      //load file info
      handleGetFileInfo(file);
    } else {
      setLoading(false);
      setFileInfo(file);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  return (
    <List.Item
      extra={
        onDelete && (
          <Space>
            <Button
              hidden={disabled}
              onClick={onDelete}
              danger
              type="link"
              icon={<DeleteFilled />}
            >
              Delete
            </Button>
          </Space>
        )
      }
    >
      <a
        style={{ width: '100%' }}
        href={fileInfo?.url}
        target="_blank"
        rel="noreferrer"
      >
        <List.Item.Meta
          avatar={
            loading ? (
              <Skeleton.Image active={loading}></Skeleton.Image>
            ) : (
              renderFile()
            )
          }
          title={
            loading ? (
              <Skeleton active={loading}></Skeleton>
            ) : (
              fileInfo?.fileName || fileInfo?.name || 'Unknown'
            )
          }
          description={loading ? null : formatBytes(fileInfo?.size || 0)}
        ></List.Item.Meta>
      </a>
    </List.Item>
  );
};

export const Uploader = ({ disabled, value, onChange }: any) => {
  const [fileList, setFileList] = useState<
    number[] | UploadFile[] | unknown[]
  >();
  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };
  const handleChange = (e) => {
    const list = normFile(e);
    setFileList(list);
    onChange(list);
  };

  const handleRemoveFileItem = (index) => {
    const newList = [...fileList];
    newList.splice(index, 1);
    setFileList(newList);
    onChange(newList);
  };

  useEffect(() => {
    setFileList(value);
  }, [value]);
  return (
    <>
      <List
        dataSource={fileList}
        renderItem={(file: number | UploadFile, index) => {
          const id = typeof file === 'number' ? file : file?.uid;
          return (
            <FileItem
              disabled={disabled}
              onDelete={() => {
                handleRemoveFileItem(index);
              }}
              file={file}
              key={id}
            ></FileItem>
          );
        }}
      ></List>
      <Upload
        showUploadList={false}
        onChange={handleChange}
        fileList={value}
        beforeUpload={() => false}
      >
        <Button disabled={disabled} style={{ paddingLeft: 0 }} type="link">
          Upload file
        </Button>
      </Upload>
    </>
  );
};
